import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
    cidadeService,
    cursoService,
    agenteIntegradorService,
    relatorioEstagioService
} from '@/services/api';
import {
    DocumentArrowDownIcon,
    FunnelIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { AgenteIntegrador, Cidade, Curso, RelatorioPersonalizadoFilters, RelatorioPersonalizadoItem, RelatorioPersonalizadoResponse } from '@/types';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

export const RelatoriosPersonalizadosPage: React.FC = () => {
    const [filters, setFilters] = useState<RelatorioPersonalizadoFilters>({ limit: 100 });
    const [showFilters, setShowFilters] = useState(true);
    const [searchText, setSearchText] = useState<string>("");
    const [exportando, setExportando] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setFilters(prev => ({ ...prev, busca: searchText || undefined }));
        }, 400);
        return () => clearTimeout(timeout);
    }, [searchText]);

    const { data: relatorioData, isLoading, isError, error, refetch, isFetching } = useQuery<RelatorioPersonalizadoResponse>({
        queryKey: ['relatorios-personalizados', filters],
        queryFn: () => relatorioEstagioService.personalizado(filters),
        staleTime: 0,
        retry: false,
        placeholderData: (previousData) => previousData,
    });

    useEffect(() => {
        if (isError) {
            const mensagem = (error as any)?.response?.data?.error || 'Não foi possível gerar o relatório personalizado';
            toast.error(mensagem);
        }
    }, [isError, error]);

    const dados: RelatorioPersonalizadoItem[] = relatorioData?.dados ?? [];
    const resumo = relatorioData?.resumo ?? {
        totalEstagios: 0,
        totalAtivos: 0,
        totalConcluidos: 0,
        totalCancelados: 0,
    };
    const permissoes = relatorioData?.permissoes;
    const exportacoesRestantes = permissoes && permissoes.limiteMensal !== null
        ? Math.max(permissoes.limiteRestante ?? 0, 0)
        : null;

    const exportarExcel = async () => {
        if (dados.length === 0) {
            toast.error('Não há dados para exportar');
            return;
        }

        try {
            setExportando(true);
            const exportResponse = await relatorioEstagioService.personalizadoExport(filters);
            const dadosExportar = exportResponse.dados;

            if (!dadosExportar || dadosExportar.length === 0) {
                toast.error('Não há dados para exportar');
                return;
            }

            const dadosParaExportar = dadosExportar.map((item) => ({
                'Aluno': item.alunoNome,
                'Curso': item.cursoNome,
                'Empresa': item.empresaNome,
                'Nome Fantasia Empresa': item.empresaNomeFantasia ?? '',
                'Agente Integrador': item.agenteIntegradorNome ?? '',
                'Cidade (Aluno)': item.cidadeAlunoNome,
                'Cidade (Empresa)': item.cidadeEmpresaNome,
                'Telefone Aluno': item.alunoTelefone ?? '',
                'E-mail Aluno': item.alunoEmail ?? '',
                'Telefone Empresa': item.empresaTelefone ?? '',
                'E-mail Empresa': item.empresaEmail ?? '',
                'Início Estágio': new Date(item.dataInicio).toLocaleDateString('pt-BR'),
                'Término Estágio': new Date(item.dataTermino).toLocaleDateString('pt-BR'),
                'Carga Horária Semanal': item.cargaHorariaSemanal,
                'Bolsa Auxílio': item.bolsaAuxilio.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                }),
                'Status': item.status,
                'Data de encerramento/cancelamento': item.dataCancelamento
                    ? new Date(item.dataCancelamento).toLocaleDateString('pt-BR')
                    : '',
                'Motivo do cancelamento': item.motivoCancelamento ?? '',
                'Dias Estágio': item.diasTotais,
                'Dias Cumprido': item.diasCumpridos,
                'Data Apólice': new Date(item.dataApolice).toLocaleDateString('pt-BR'),
                'Restantes': item.diasRestantes,
            }));

            const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatórios');

            const maxWidth = 30;
            const colWidths = Object.keys(dadosParaExportar[0] || {}).map(() => ({ wch: maxWidth }));
            worksheet['!cols'] = colWidths;

            const fileName = `relatorios-estagio-${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            toast.success('Relatório exportado com sucesso!');
            await refetch();
        } catch (err: any) {
            const mensagem = err?.response?.data?.error || 'Erro ao exportar relatório';
            console.error('Erro ao exportar:', err);
            toast.error(mensagem);
        } finally {
            setExportando(false);
        }
    };

    const handleFilterChange = (key: keyof RelatorioPersonalizadoFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value === '' || value === undefined ? undefined : value,
        }));
    };

    const handleBuscaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const limparFiltros = () => {
        setFilters({ limit: 100 });
        setSearchText("");
    };

    const formatCurrency = (value: number) => value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });

    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('pt-BR');

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Ativo':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'Cancelado':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'Concluido':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    if (isLoading && !relatorioData) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando dados dos relatórios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        📊 Relatórios de Estágio Personalizados
                    </h1>
                    <p className="mt-1 text-gray-600">
                        Visualize e exporte dados consolidados dos estágios
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => setShowFilters(!showFilters)}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <FunnelIcon className="h-5 w-5" />
                        {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
                    </Button>
                    <Button
                        onClick={exportarExcel}
                        disabled={dados.length === 0 || exportando}
                        className="bg-green-600 hover:bg-green-700 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <DocumentArrowDownIcon className="h-5 w-5" />
                        {exportando ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                        <div className="text-sm font-medium text-gray-600">Total de Estágios</div>
                        <div className="text-2xl font-bold text-gray-900">{resumo.totalEstagios}</div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                        <div className="text-sm font-medium text-gray-600">Ativos</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {resumo.totalAtivos}
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                        <div className="text-sm font-medium text-gray-600">Concluídos</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {resumo.totalConcluidos}
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                        <div className="text-sm font-medium text-gray-600">Cancelados</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {resumo.totalCancelados}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {showFilters && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            <span>Filtros</span>
                            {(filters.busca || filters.cidadeId || filters.cursoId || filters.agenteIntegradorId || filters.status || filters.dataInicio || filters.dataFim || filters.anoInicio) && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={limparFiltros}
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <XMarkIcon className="h-4 w-4 mr-1" />
                                    Limpar
                                </Button>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    🔍 Buscar por nome
                                </label>
                                <input
                                    type="text"
                                    placeholder="Digite o nome do aluno, curso ou empresa..."
                                    value={searchText}
                                    onChange={handleBuscaChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Busca por nome do aluno, curso ou empresa
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Cidade
                                    </label>
                                    <ServerSearchSelect<Cidade>
                                        value={filters.cidadeId ?? null}
                                        onChange={(selectedId) => handleFilterChange('cidadeId', selectedId)}
                                        fetchPage={(params) => cidadeService.list(params)}
                                        fetchById={(selectedId) => cidadeService.getById(selectedId)}
                                        getOptionLabel={(cidade) => `${cidade.nome} — ${cidade.uf}`}
                                        queryKey="cidades-relatorios-personalizados-filter"
                                        placeholder="Todas as cidades"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Curso
                                    </label>
                                    <ServerSearchSelect<Curso>
                                        value={filters.cursoId ?? null}
                                        onChange={(selectedId) => handleFilterChange('cursoId', selectedId)}
                                        fetchPage={(params) => cursoService.list(params)}
                                        fetchById={(selectedId) => cursoService.getById(selectedId)}
                                        getOptionLabel={(curso) => curso.nome}
                                        queryKey="cursos-relatorios-personalizados-filter"
                                        placeholder="Todos os cursos"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status || ''}
                                        onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Todos</option>
                                        <option value="Ativo">Ativo</option>
                                        <option value="Cancelado">Cancelado</option>
                                        <option value="Concluido">Concluído</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Agente Integrador
                                    </label>
                                    <ServerSearchSelect<AgenteIntegrador>
                                        value={filters.agenteIntegradorId ?? null}
                                        onChange={(selectedId) => handleFilterChange('agenteIntegradorId', selectedId)}
                                        fetchPage={(params) => agenteIntegradorService.list(params)}
                                        fetchById={(selectedId) => agenteIntegradorService.getById(selectedId)}
                                        getOptionLabel={(agente) => agente.nome}
                                        queryKey="agentes-relatorios-personalizados-filter"
                                        placeholder="Todos os agentes"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Data Início (estágio)
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dataInicio || ''}
                                        onChange={(e) => handleFilterChange('dataInicio', e.target.value || undefined)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Data Fim (estágio)
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dataFim || ''}
                                        onChange={(e) => handleFilterChange('dataFim', e.target.value || undefined)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Ano de Início
                                    </label>
                                    <select
                                        value={filters.anoInicio || ''}
                                        onChange={(e) => handleFilterChange('anoInicio', e.target.value ? Number(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Todos os anos</option>
                                        {Array.from({ length: 15 }, (_, i) => {
                                            const ano = new Date().getFullYear() - 5 + i;
                                            return (
                                                <option key={ano} value={ano}>
                                                    {ano}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Filtra estágios que iniciaram no ano selecionado
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Dados dos Estágios</CardTitle>
                    <CardDescription>
                        {dados.length} registro(s) encontrado(s)
                        {exportacoesRestantes !== null && (
                            <span className="ml-2 text-xs text-gray-500">
                                {`Exportações restantes: ${exportacoesRestantes}`}
                            </span>
                        )}
                    </CardDescription>
                </CardHeader>
                <CardContent>

                    {dados.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Nenhum estágio encontrado
                            </h3>
                            <p className="text-gray-500">
                                Ajuste os filtros para visualizar os dados
                            </p>
                        </div>
                    ) : (
                        <div className="relative overflow-x-auto">
                            {isFetching && (
                                <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
                                    <div className="flex flex-col items-center gap-2 text-gray-600">
                                        <div className="h-10 w-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-sm">Atualizando dados...</span>
                                    </div>
                                </div>
                            )}
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Aluno
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Curso
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Empresa
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nome Fantasia Empresa
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Agente Integrador
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cidade (Aluno)
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Telefone Aluno
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            E-mail Aluno
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cidade (Empresa)
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Telefone Empresa
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            E-mail Empresa
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Início Estágio
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Término Estágio
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Carga H. Semanal
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Bolsa Auxílio
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Data de encerramento/cancelamento
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Motivo do cancelamento
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Dias Estágio
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Dias Cumprido
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Data Apólice
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Restantes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {dados.map((item, index) => (
                                        <tr key={item.estagioId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.alunoNome}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.cursoNome}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.empresaNome}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.empresaNomeFantasia ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.agenteIntegradorNome ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.cidadeAlunoNome}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.alunoTelefone ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.alunoEmail ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.cidadeEmpresaNome}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.empresaTelefone ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.empresaEmail ?? '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {formatDate(item.dataInicio)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {formatDate(item.dataTermino)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                                {item.cargaHorariaSemanal}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {formatCurrency(item.bolsaAuxilio)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {item.dataCancelamento ? formatDate(item.dataCancelamento) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                                                {item.motivoCancelamento || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                                {item.diasTotais}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                                                {item.diasCumpridos}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                {formatDate(item.dataApolice)}
                                            </td>
                                            <td className={`px-4 py-3 whitespace-nowrap text-sm text-center font-bold ${item.diasRestantes === 0 ? 'bg-yellow-300 text-gray-900' : 'text-gray-700'}`}>
                                                {item.diasRestantes}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
